// Icon — Heroicons v2 (outline) behind the app's own stable `name` API.
//
// The 50+ `IconName` values below are the vocabulary the app has always used
// (1,500+ call sites). They now resolve to Heroicons rather than hand-drawn
// paths, so the icon set is consistent, professionally hinted, and gets fixes
// upstream — but no call site had to change.
//
// Everything the old component guaranteed still holds:
//   • 24×24 grid, stroke-based, `currentColor` — an icon inherits the text
//     color of whatever it sits in and re-themes with the tenant brand.
//   • Decorative by default (aria-hidden); pass `title` to make it a
//     labelled graphic (role="img" + <title>).
//   • `size` in px, `strokeWidth` on the 24-grid, `flexShrink: 0`.
//
//   <Icon name="calendar" />
//   <Icon name="alert" size={18} title="Warning" />
//   <button className="ui-btn ui-btn--primary"><Icon name="plus" size={16}/> Add</button>

import { SVGProps, ComponentType } from 'react';
import {
  AcademicCapIcon, ArrowDownTrayIcon, ArrowPathIcon, ArrowRightIcon,
  ArrowTopRightOnSquareIcon, ArrowTrendingDownIcon, ArrowTrendingUpIcon,
  ArrowUpTrayIcon, BanknotesIcon, Bars3Icon, BellIcon, BoltIcon, BriefcaseIcon,
  BuildingOffice2Icon, BuildingOfficeIcon, CalendarDaysIcon, ChartBarIcon,
  ChartPieIcon, ChatBubbleLeftRightIcon, CheckCircleIcon, CheckIcon,
  ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ClipboardDocumentListIcon,
  ClockIcon, Cog6ToothIcon, DocumentTextIcon, EllipsisHorizontalIcon,
  EnvelopeIcon, ExclamationCircleIcon, ExclamationTriangleIcon, EyeIcon,
  FunnelIcon, HomeIcon, InformationCircleIcon, LinkIcon, LockClosedIcon,
  MagnifyingGlassIcon, MoonIcon, PencilSquareIcon, PhoneIcon, PlusIcon,
  ShieldCheckIcon, SparklesIcon, Squares2X2Icon, StarIcon, SunIcon, TrashIcon,
  UserGroupIcon, UserIcon, UsersIcon, XMarkIcon,
  // — added for the sidebar navigation vocabulary —
  ArchiveBoxIcon, ArrowRightOnRectangleIcon, BellSlashIcon, BookOpenIcon,
  BugAntIcon, BuildingLibraryIcon, ChatBubbleLeftEllipsisIcon, CircleStackIcon,
  ClipboardDocumentCheckIcon, CpuChipIcon, DocumentDuplicateIcon, FilmIcon,
  FingerPrintIcon, FlagIcon, FolderIcon, GiftIcon, GlobeAltIcon, HeartIcon,
  InboxArrowDownIcon, KeyIcon, ListBulletIcon, MapIcon, MapPinIcon,
  MegaphoneIcon, NoSymbolIcon, PaperAirplaneIcon, PaperClipIcon,
  PauseCircleIcon, PuzzlePieceIcon, QuestionMarkCircleIcon, ReceiptPercentIcon,
  ScaleIcon, SignalIcon, SquaresPlusIcon, TagIcon, TrophyIcon, UserCircleIcon,
  ViewColumnsIcon, ViewfinderCircleIcon,
  PhotoIcon, TableCellsIcon,
} from '@heroicons/react/24/outline';

export type IconName =
  | 'dashboard' | 'home' | 'candidates' | 'user' | 'users' | 'jobs' | 'clients'
  | 'calendar' | 'clock' | 'document' | 'clipboard' | 'onboarding' | 'placement'
  | 'check' | 'check-circle' | 'alert' | 'alert-circle' | 'info' | 'x'
  | 'bell' | 'search' | 'settings' | 'chart' | 'pie' | 'trending-up' | 'trending-down'
  | 'money' | 'shield' | 'mail' | 'phone' | 'message' | 'plus' | 'edit' | 'trash'
  | 'download' | 'upload' | 'filter' | 'refresh' | 'eye' | 'lock' | 'star'
  | 'building' | 'sparkles' | 'pulse' | 'sun' | 'moon' | 'menu' | 'more'
  | 'chevron-down' | 'chevron-right' | 'chevron-left' | 'arrow-right' | 'external'
  // — sidebar navigation vocabulary —
  | 'archive' | 'bell-slash' | 'book' | 'bug' | 'clipboard-check' | 'columns'
  | 'cpu' | 'database' | 'document-duplicate' | 'door' | 'film' | 'fingerprint'
  | 'flag' | 'folder' | 'gift' | 'globe' | 'heart' | 'inbox' | 'integrations'
  | 'key' | 'library' | 'link' | 'list' | 'map' | 'map-pin' | 'megaphone'
  | 'no-symbol' | 'pause' | 'paperclip' | 'puzzle' | 'help' | 'receipt'
  | 'scale' | 'send' | 'signal' | 'tag' | 'target' | 'trophy' | 'user-circle'
  | 'chat-single'
  // — file-type vocabulary (attachment / upload lists) —
  | 'image' | 'table';

type HeroIcon = ComponentType<SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;

// App vocabulary → Heroicons. Where the app's word is domain-specific
// ('placement', 'pulse', 'onboarding') the mapping picks the Heroicon whose
// existing use in the app matches: placement = the link between candidate and
// role, pulse = run-now / live activity, onboarding = training.
const ICONS: Record<IconName, HeroIcon> = {
  dashboard: Squares2X2Icon,
  home: HomeIcon,
  candidates: UserGroupIcon,
  user: UserIcon,
  users: UsersIcon,
  jobs: BriefcaseIcon,
  clients: BuildingOffice2Icon,
  calendar: CalendarDaysIcon,
  clock: ClockIcon,
  document: DocumentTextIcon,
  clipboard: ClipboardDocumentListIcon,
  onboarding: AcademicCapIcon,
  placement: LinkIcon,
  check: CheckIcon,
  'check-circle': CheckCircleIcon,
  alert: ExclamationTriangleIcon,
  'alert-circle': ExclamationCircleIcon,
  info: InformationCircleIcon,
  x: XMarkIcon,
  bell: BellIcon,
  search: MagnifyingGlassIcon,
  settings: Cog6ToothIcon,
  chart: ChartBarIcon,
  pie: ChartPieIcon,
  'trending-up': ArrowTrendingUpIcon,
  'trending-down': ArrowTrendingDownIcon,
  money: BanknotesIcon,
  shield: ShieldCheckIcon,
  mail: EnvelopeIcon,
  phone: PhoneIcon,
  message: ChatBubbleLeftRightIcon,
  plus: PlusIcon,
  edit: PencilSquareIcon,
  trash: TrashIcon,
  download: ArrowDownTrayIcon,
  upload: ArrowUpTrayIcon,
  filter: FunnelIcon,
  refresh: ArrowPathIcon,
  eye: EyeIcon,
  lock: LockClosedIcon,
  star: StarIcon,
  building: BuildingOfficeIcon,
  sparkles: SparklesIcon,
  pulse: BoltIcon,
  sun: SunIcon,
  moon: MoonIcon,
  menu: Bars3Icon,
  more: EllipsisHorizontalIcon,
  'chevron-down': ChevronDownIcon,
  'chevron-right': ChevronRightIcon,
  'chevron-left': ChevronLeftIcon,
  'arrow-right': ArrowRightIcon,
  external: ArrowTopRightOnSquareIcon,

  // — sidebar navigation vocabulary —
  archive: ArchiveBoxIcon,
  'bell-slash': BellSlashIcon,
  book: BookOpenIcon,
  bug: BugAntIcon,
  'clipboard-check': ClipboardDocumentCheckIcon,
  columns: ViewColumnsIcon,
  cpu: CpuChipIcon,
  database: CircleStackIcon,
  'document-duplicate': DocumentDuplicateIcon,
  door: ArrowRightOnRectangleIcon,
  film: FilmIcon,
  fingerprint: FingerPrintIcon,
  flag: FlagIcon,
  folder: FolderIcon,
  gift: GiftIcon,
  globe: GlobeAltIcon,
  heart: HeartIcon,
  inbox: InboxArrowDownIcon,
  integrations: SquaresPlusIcon,
  key: KeyIcon,
  library: BuildingLibraryIcon,
  // Same glyph as 'placement', deliberately: 'link' reads as a URL/webhook,
  // 'placement' as a candidate↔role pairing. Distinct words, distinct intent.
  link: LinkIcon,
  list: ListBulletIcon,
  map: MapIcon,
  'map-pin': MapPinIcon,
  megaphone: MegaphoneIcon,
  'no-symbol': NoSymbolIcon,
  pause: PauseCircleIcon,
  paperclip: PaperClipIcon,
  puzzle: PuzzlePieceIcon,
  help: QuestionMarkCircleIcon,
  receipt: ReceiptPercentIcon,
  scale: ScaleIcon,
  send: PaperAirplaneIcon,
  signal: SignalIcon,
  tag: TagIcon,
  target: ViewfinderCircleIcon,
  trophy: TrophyIcon,
  'user-circle': UserCircleIcon,
  'chat-single': ChatBubbleLeftEllipsisIcon,
  image: PhotoIcon,
  table: TableCellsIcon,
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  /** px — width & height. Default 18. */
  size?: number;
  /** stroke width in the 24-grid. Default 1.75. */
  strokeWidth?: number;
  /** When set, the icon is a labelled graphic instead of decorative. */
  title?: string;
}

export default function Icon({ name, size = 18, strokeWidth = 1.75, title, style, ...rest }: IconProps) {
  const Glyph = ICONS[name];
  // An unknown name would otherwise blank-render; fail visibly in dev only.
  if (!Glyph) {
    if (import.meta.env.DEV) console.warn(`[Icon] unknown name: "${name}"`);
    return null;
  }
  return (
    <Glyph
      width={size}
      height={size}
      strokeWidth={strokeWidth}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      focusable="false"
      style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', ...style }}
      {...rest}
    />
  );
}
